import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link, useParams, useHistory } from 'react-router-dom';
import { RootState } from 'modules';
import { getSubThemeDetail } from 'libs/api/learning';
import { usePermission } from 'libs/hooks';
import { TSubTheme, TLearningParams } from 'modules/learning';
import { CFINANCE_THEME, CMAIN_THEMES } from 'constants/learningboard.const';
import { ERoute } from 'enums/route.enum';
import { Button, Row, Typography } from 'antd';
import styled from 'styled-components';
import { device, PrimaryColor, SecondaryColor } from 'GlobalStyles';
import RecommendSubjectBox from '../parts/RecommendSubject';
import SubjectTemplate from '../parts/SubjectTemplate';
import EditSubcategoryModal from './EditSubcategoryModal';

const { Title, Text } = Typography;

const SubThemeBoard = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const history = useHistory();
  const [subThemes, setSubThemes] = useState<Array<TSubTheme>>([]);
  const { category, operation }: TLearningParams = useParams();
  const [subcategoryModal, setSubcategoryModal] = useState<{
    visible: boolean;
  }>({
    visible: false,
  });
  const { adminPermission } = usePermission();

  class ThemesInfo {
    static getInfo(info: string | number) {
      const themeInfo: Array<any> =
        currentUser?.primary_field === 'finance'
          ? CFINANCE_THEME.filter(({ link }) => link === category)
          : CMAIN_THEMES.filter(({ link }) => link === category);
      if (themeInfo.length > 0) {
        return themeInfo[0][info];
      }
    }
  }

  const getSubThemes = async () => {
    const { success, data } = await getSubThemeDetail({
      subject: `${category}`,
      is_example: 'False',
      mentoring_field: currentUser?.primary_field === 'finance' ? 'finance' : 'consulting',
    });

    if (success) {
      setSubThemes(data);
    }
  };

  useEffect(() => {
    if (!currentUser?.primary_field) {
      getSubThemes();
      return;
    } else if (category !== currentUser?.primary_field) {
      let getPrimaryField =
        category === 'fit_interview' ||
        category === 'turnaround_strategy' ||
        category === 'case_interview' ||
        category === 'growth_strategy' ||
        category === 'dd_market_sizing' ||
        category === 'operation_optimization' ||
        category === 'sales_marketing_strategy'
          ? 'consulting'
          : 'finance';

      if (currentUser?.primary_field !== getPrimaryField) {
        history.push(`${ERoute.CSLearningBoard}`);
      } else {
        getSubThemes();
      }
    }
  }, [category, operation]);

  return (
    <SubjectTemplate pageTitle={ThemesInfo.getInfo('name')} pageSubtitle={ThemesInfo.getInfo('subtitle')}>
      {adminPermission && (
        <StyledAdminBtns>
          <Row>
            <Link to={`${ERoute.CSLearningBoard}/${category}/create`}>
              <Button type='primary' style={{ marginRight: '10px' }}>
                글쓰기
              </Button>
            </Link>
            <Button type='primary' onClick={() => setSubcategoryModal({ visible: true })}>
              Subcategory 관리
            </Button>
          </Row>

          <EditSubcategoryModal modalState={subcategoryModal} setModalState={setSubcategoryModal} />
        </StyledAdminBtns>
      )}

      {subThemes.length > 0 && (
        <StyledCardList>
          {subThemes.map(({ id, title, subtitle, subject, thumbnail }: TSubTheme) => (
            <StyledCard key={id}>
              <StyledCardIcon src={thumbnail.location} />
              <StyledCardInfomation>
                <StyledCardTitle>{title}</StyledCardTitle>
                {subtitle && (
                  <StyledCardIntroduction
                    dangerouslySetInnerHTML={{
                      __html: subtitle.replace('<br>', ''),
                    }}
                  />
                )}

                <Link to={`${ERoute.CSLearningBoard}/${subject}/${id}/view`}>
                  <Button className='ant-btn-primary'>읽기</Button>
                </Link>
              </StyledCardInfomation>
            </StyledCard>
          ))}
        </StyledCardList>
      )}

      {!currentUser?.is_finance_frameworks_membership && category === 'basic_guide' ? (
        <PriceGuideContainer>
          <PriceGuideBox>
            <GuidePriceHeader style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Title>Finance Contents 구매하기</Title>
            </GuidePriceHeader>

            <GuidePriceTickets>
              <div>
                <PriceGuideTicket>
                  <Title>Finance Frameworks 무제한 이용권</Title>

                  <Text>
                    <span className='discount'>정가 5만원</span>
                    <span className='price'>3</span>
                    <span className='priceTag'>만원 (40% 할인) </span>
                    <span className='period'>/ 3개월</span>
                  </Text>
                </PriceGuideTicket>

                {!currentUser?.is_finance_frameworks_membership && (
                  <Link to={'/purchase/finance_frameworks'}>
                    <Button className='secondary'>구매하기</Button>
                  </Link>
                )}
              </div>
            </GuidePriceTickets>
          </PriceGuideBox>
        </PriceGuideContainer>
      ) : (
        <RecommendSubjectBox />
      )}
    </SubjectTemplate>
  );
};

export default SubThemeBoard;

const StyledAdminBtns = styled.div`
  width: 790px;
  margin-top: 10px;

  @media screen and ${device.lg} {
    width: 520px;
  }

  @media screen and ${device.sm} {
    width: 270px;
  }
`;

const StyledCardList = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  width: 810px;
  margin-top: 50px;
  background-color: #f9f9f9;

  @media screen and ${device.lg} {
    width: 540px;
  }

  @media screen and ${device.sm} {
    width: 270px;
  }
`;

const StyledCard = styled.div`
  width: 254px;
  margin: 0 8px 27px 8px;
`;

const StyledCardIcon = styled.img`
  width: 100%;
  height: 195px;
  border-radius: 10px 10px 0px 0px;
  object-fit: cover;
`;

const StyledCardInfomation = styled.div`
  padding: 20px 25px 20px 25px;
  border-radius: 0px 0px 10px 10px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
  background-color: #ffffff;

  .ant-btn-primary {
    width: 100%;
  }
`;

const StyledCardTitle = styled.div`
  display: -webkit-box;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const StyledCardIntroduction = styled.div`
  display: -webkit-box;
  height: 100px;
  margin: 10px 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #14112a;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const PriceGuideContainer = styled.section`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: 50px 50px 70px 50px;

  @media screen and ${device.lg} {
    padding: 35px 20px;
  }

  button.secondary {
    display: block;
    width: 134px;
    height: 44px;
    margin: 0 auto;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }
`;

const PriceGuideBox = styled.div`
  margin-top: 25px;
  padding: 30px 45px;
  border: 1px solid ${SecondaryColor};
  border-radius: 10px;
  background: #ffffff;

  @media screen and ${device.lg} {
    padding: 25px 15px;
  }

  .secondSubTitle {
    display: block;
    margin: 10px 0 0 0;
    color: ${PrimaryColor};
  }
`;

const GuidePriceHeader = styled.header`
  display: flex;
  align-items: center;

  @media screen and ${device.lg} {
    flex-direction: column;
    align-items: flex-start;
  }

  h1.ant-typography {
    margin: 0 10px 0 0;
    color: #141121;
    font-size: 20px;
  }

  span.ant-typography {
    color: #747474;
  }
`;

const GuidePriceTickets = styled.div`
  display: flex;
  justify-content: center;

  @media screen and ${device.lg} {
    flex-direction: column;
  }

  .ant-btn.secondary.purchased {
    width: auto;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }
`;

const PriceGuideTicket = styled.div`
  width: 356px;
  margin: 15px 15px;
  padding: 25px 0;
  border-radius: 10px;
  background: #f9f9f9;
  white-space: pre-wrap;

  @media screen and ${device.xl} {
    width: 260px;
  }

  @media screen and ${device.lg} {
    width: 100%;
    margin: 25px 0;
  }

  h1.ant-typography {
    margin: 0;
    font-size: 18px;
    text-align: center;
    line-height: 26px;
    color: ${PrimaryColor};

    @media screen and ${device.xl} {
      font-size: 16px;
    }

    @media screen and ${device.lg} {
      font-size: 18px;
    }
  }

  span.ant-typography {
    display: block;
    text-align: center;

    .price {
      margin-right: 5px;
      font-size: 48px;
      color: ${SecondaryColor};
    }

    .priceTag {
      font-size: 18px;
      color: ${SecondaryColor};
    }

    .period,
    .discount {
      font-size: 18px;
      color: #747474;
    }

    .discount {
      margin-right: 10px;
      text-decoration: line-through;
    }
  }
`;
